import { FormApi } from "final-form";
import React from "react";
import { Field } from "react-final-form";
import { Button, Col, Row, Badge, Spinner } from "reactstrap";
import { CustomerBonusInfoDto } from "../../store/models/TouchpointDto";
import { IssuedCouponDto, getCouponTypeColor } from "../../store/models/CouponTemplateDto";
import { CashDeskSimulatorProps, calculatePaymentAmount } from "./CashDeskSimulator";
import { formatEuro } from "../app/Utils";
import API from "../../api/Api";

interface CouponPanelProps {
  customerBonusInfo?: CustomerBonusInfoDto;
  form: FormApi<CashDeskSimulatorProps, Partial<CashDeskSimulatorProps>>;
}

interface CouponPanelState {
  activatedCoupons: IssuedCouponDto[];
  loading: boolean;
  error: string | null;
}

export class CouponPanel extends React.Component<CouponPanelProps, CouponPanelState> {
  constructor(props: CouponPanelProps) {
    super(props);
    this.state = {
      activatedCoupons: [],
      loading: false,
      error: null,
    };
  }

  componentDidMount() {
    this.loadActivatedCoupons();
  }

  loadActivatedCoupons = async () => {
    try {
      this.setState({ loading: true, error: null });
      const response = await API.getActivatedCoupons();
      this.setState({ activatedCoupons: response.data });
    } catch (error) {
      this.setState({ error: "Fehler beim Laden der aktivierten Coupons" });
    } finally {
      this.setState({ loading: false });
    }
  };

  getCouponDisplayLabel = (coupon: IssuedCouponDto): string => {
    const template = coupon.couponTemplate;
    if (!template) return "Coupon";
    
    switch (template.valueType) {
      case "PERCENTAGE":
        return `${template.discountPercentage}% Rabatt`;
      case "FIXED_AMOUNT":
        return `€${template.discountAmount} Rabatt`;
      case "FREE_SHIPPING":
        return "Versandkostenfrei";
      case "GIFT":
        return "Geschenk";
      default:
        return "Coupon";
    }
  };

  calculateCouponSavings = (coupon: IssuedCouponDto, paymentAmount: number): number => {
    const template = coupon.couponTemplate;
    if (!template || !paymentAmount) return 0;
    
    switch (template.valueType) {
      case "PERCENTAGE":
        return (paymentAmount * (template.discountPercentage || 0)) / 100;
      case "FIXED_AMOUNT":
        return Math.min(template.discountAmount || 0, paymentAmount);
      case "FREE_SHIPPING":
        // Calculate shipping savings based on order amount (typically 3-8% or €4.99-€9.99)
        return Math.max(4.99, Math.min(paymentAmount * 0.05, 12.99));
      case "GIFT":
        // Calculate gift value based on order amount (typically 5-15% or €5-€25)
        return Math.max(5, Math.min(paymentAmount * 0.10, 25));
      default:
        return 0;
    }
  };

  getSavingsDescription = (coupon: IssuedCouponDto, paymentAmount: number): string => {
    const template = coupon.couponTemplate;
    if (!template) return "";
    
    switch (template.valueType) {
      case "PERCENTAGE":
        return `(${template.discountPercentage}% of ${formatEuro(paymentAmount)})`;
      case "FIXED_AMOUNT":
        return "(fixed discount)";
      case "FREE_SHIPPING":
        return "(shipping cost waived)";
      case "GIFT":
        return "(complimentary gift included)";
      default:
        return "";
    }
  };


  render() {
    const { form } = this.props;
    const { activatedCoupons, loading, error } = this.state;

    if (loading) {
      return (
        <Row>
          <Col md={12}>
            <div className="text-center py-3">
              <Spinner color="primary" size="sm" className="me-2" />
              <span>Lade aktivierte Coupons...</span>
            </div>
          </Col>
        </Row>
      );
    }

    if (error) {
      return (
        <Row>
          <Col md={12}>
            <div className="text-danger p-3 border rounded bg-light">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
              <Button size="sm" color="link" onClick={this.loadActivatedCoupons}>
                Erneut versuchen
              </Button>
            </div>
          </Col>
        </Row>
      );
    }
    
    return activatedCoupons.length ? (
      <>
        <Row className="mb-3">
          <Col md={12}>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div>
                <h5 className="mb-1">Coupon für Kauf auswählen</h5>
                <small className="text-muted">Wähle einen aktivierten Coupon aus</small>
              </div>
              <Badge color="success" pill className="px-3 py-2">
                <i className="bi bi-ticket-perforated me-2"></i>
                {activatedCoupons.length} Verfügbar
              </Badge>
            </div>
          </Col>
        </Row>
        
        <Field name="selectedCoupon" type="field">
          {(couponProps) => {
            const selectedCoupon = couponProps.input.value;
            if (selectedCoupon && form.getState().values.receiptDetailIds?.length) {
              const paymentAmount = calculatePaymentAmount(form.getState().values.receiptDetailIds);
              const template = selectedCoupon.couponTemplate;
              
              // Handle different coupon types consistently              
              if (template.valueType === 'FREE_SHIPPING') {
                return (
                  <Row className="mb-3">
                    <Col md={12}>
                      <div style={{ fontSize: '0.95rem' }}>
                        <i className="bi bi-truck me-2 text-success"></i>
                        <strong className="text-success">Versandkostenfrei ist in deiner Bestellung enthalten</strong>
                      </div>
                    </Col>
                  </Row>
                );
              } else if (template.valueType === 'GIFT') {
                return (
                  <Row className="mb-3">
                    <Col md={12}>
                      <div style={{ fontSize: '0.95rem' }}>
                        <i className="bi bi-gift me-2 text-success"></i>
                        <strong className="text-success">Du erhältst 1 kostenloses Geschenk</strong> zu deiner Bestellung
                      </div>
                    </Col>
                  </Row>
                );
              } else {
                // Handle monetary savings (PERCENTAGE, FIXED_AMOUNT)
                const savings = this.calculateCouponSavings(selectedCoupon, paymentAmount);
                
                return (
                  <Row className="mb-3">
                    <Col md={12}>
                      <div style={{ fontSize: '0.95rem' }}>
                        <i className="bi bi-piggy-bank me-2 text-success"></i>
                        Du sparst <strong className="text-success">{formatEuro(savings)}</strong> {this.getSavingsDescription(selectedCoupon, paymentAmount)}
                      </div>
                    </Col>
                  </Row>
                );
              }
            }
            return null;
          }}
        </Field>
        
        <Row>
          <Field name="selectedCoupon" type="field">
            {(props) =>
              activatedCoupons.map((coupon) => {
                const isSelected = props.input.value?.couponNumber === coupon.couponNumber;
                const typeColors = getCouponTypeColor(coupon.couponTemplate?.valueType || '');
                return (
                  <Col lg={4} md={6} sm={12} key={coupon.couponNumber} className="mb-4">
                    <button
                      type="button"
                      className={`coupon-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => {
                        form.mutators.clearCashValue();
                        props.input.onChange(isSelected ? null : coupon);
                      }}
                      style={{
                        cursor: 'pointer',
                        position: 'relative',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        padding: 0,
                        textAlign: 'left',
                        width: '100%',
                        background: isSelected 
                          ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'
                          : 'white',
                        border: isSelected ? '3px solid #28a745' : '2px solid #e9ecef',
                        boxShadow: isSelected 
                          ? '0 8px 25px rgba(40, 167, 69, 0.3)' 
                          : '0 4px 12px rgba(0, 0, 0, 0.1)',
                        transform: isSelected ? 'translateY(-2px)' : 'none',
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {/* Coupon Header */}
                      <div 
                        className="coupon-header p-3" 
                        style={{
                          background: isSelected 
                            ? 'rgba(255, 255, 255, 0.2)'
                            : typeColors.gradient,
                          color: 'white',
                          position: 'relative'
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <div className="coupon-value" style={{ 
                              fontSize: '1.5rem', 
                              fontWeight: 'bold',
                              textShadow: '0 1px 3px rgba(0,0,0,0.3)'
                            }}>
                              {this.getCouponDisplayLabel(coupon)}
                            </div>
                          </div>
                          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                            <small style={{ 
                              opacity: isSelected ? 0.9 : 0.8,
                              fontSize: '0.8rem',
                              color: isSelected ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.8)'
                            }}>
                              <i className="bi bi-hash me-1"></i>
                              ID: {coupon.couponNumber.slice(-8)}
                            </small>
                            {isSelected && (
                              <div className="selection-badge">
                                <i className="bi bi-check-circle-fill" style={{ fontSize: '1.5rem' }}></i>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Coupon Body */}
                      <div className="coupon-body p-3" style={{ 
                        color: isSelected ? 'white' : '#333',
                        background: isSelected ? 'transparent' : 'white'
                      }}>
                        {coupon.code && (
                          <div className="mb-2">
                            <div style={{ 
                              opacity: isSelected ? 0.9 : 0.8,
                              fontSize: '0.85rem',
                              display: 'flex',
                              alignItems: 'center'
                            }}>
                              <i className="bi bi-qr-code me-2" style={{ 
                                color: isSelected ? 'rgba(255,255,255,0.9)' : typeColors.primary 
                              }}></i>
                              <code style={{ 
                                background: isSelected ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                                color: isSelected ? 'white' : '#333',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '0.8rem',
                                fontFamily: 'monospace'
                              }}>
                                {coupon.code}
                              </code>
                            </div>
                          </div>
                        )}
                        
                        <div className="expiry-info d-flex align-items-center" style={{
                          fontSize: '0.85rem',
                          opacity: isSelected ? 0.9 : 0.8
                        }}>
                          <i className="bi bi-calendar-event me-2"></i>
                          <span>Läuft ab {new Date(coupon.expiresAt).toLocaleDateString()}</span>
                        </div>
                      </div>

                      {/* Decorative elements */}
                      <div 
                        className="coupon-decoration"
                        style={{
                          position: 'absolute',
                          top: '50%',
                          right: '-10px',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          background: '#f8f9fa',
                          transform: 'translateY(-50%)',
                          border: '2px solid #e9ecef'
                        }}
                      ></div>
                      <div 
                        className="coupon-decoration"
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '-10px',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          background: '#f8f9fa',
                          transform: 'translateY(-50%)',
                          border: '2px solid #e9ecef'
                        }}
                      ></div>
                    </button>
                  </Col>
                );
              })
            }
          </Field>
        </Row>
      </>
    ) : (
      <Row>
        <Col md={12}>
          <div className="no-coupons-state text-center py-5">
            <div className="mb-3">
              <i className="bi bi-ticket-perforated" style={{ 
                fontSize: '3rem', 
                color: '#6c757d',
                opacity: 0.5 
              }}></i>
            </div>
            <h5 className="text-muted mb-2">Keine aktivierten Coupons</h5>
            <p className="text-muted mb-3">Dieser Kunde hat keine aktivierten Coupons zur Verwendung.</p>
            <Button color="primary" size="sm" onClick={this.loadActivatedCoupons}>
              <i className="bi bi-arrow-clockwise me-2"></i>
              Coupons aktualisieren
            </Button>
          </div>
        </Col>
      </Row>
    );
  }
}
